import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createAdminStaff,
  createStaff,
  deleteStaff,
  getStaff,
  getAllStaff,
  getStaffById,
  updateStaff,
  staffLogin,
  staffLogout,
  forgotPassword,
  resetPassword,
  createStaffNotification,
  staffVerifyOtp,
  staffResendOtp,
  sendEmailOtp,
  staffSendEmailChangeOtp,
  staffChangeEmail,
} from './staffApi';
import { toast } from 'react-toastify';

export const useGetStaff = () => {
  return useQuery({ queryKey: ['user'], queryFn: getStaff });
};

export const useGetAllStaff = () => {
  return useQuery({ queryKey: ['staff'], queryFn: getAllStaff });
};

export const useGetStaffById = (staffId) => {
  return useQuery({
    queryKey: ['staff', staffId],
    queryFn: () => getStaffById(staffId),
    enabled: !!staffId,
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create_staff'],
    mutationFn: createStaff,
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member added successfully');
    },
    onError: (error) => {
      console.error('Error creating staff:', error);
      toast.error(
        error.response?.data?.message || 'Failed to add staff member'
      );
    },
  });
};

export const useCreateAdminStaff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create_admin_staff'],
    mutationFn: createAdminStaff,
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update_staff'],
    mutationFn: ([id, data]) => updateStaff(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member updated successfully');
    },
    onError: (error) => {
      console.error('Error updating staff:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update staff member'
      );
    },
  });
};

export const useUpdateLoggedInStaff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update__logged_in_staff'],
    mutationFn: ([id, data]) => updateStaff(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['user']);
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete_staff'],
    mutationFn: deleteStaff,
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting staff:', error);
      toast.error(
        error.response?.data?.message || 'Failed to delete staff member'
      );
    },
  });
};

export const useVerifyOtp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['verify_otp'],
    mutationFn: staffVerifyOtp,
    onSuccess: () => {
      queryClient.invalidateQueries(['user']);
    },
    onError: (error) => {
      console.error('Verify OTP error:', error);
      toast.error(error.response?.data?.message || 'Failed to verify code');
    },
  });
};

export const useResendOtp = () => {
  return useMutation({
    mutationKey: ['resend_otp'],
    mutationFn: staffResendOtp,
    onError: (error) => {
      console.error('Resend OTP error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to resend verification code'
      );
    },
  });
};

export const useSendEmailOtp = () => {
  return useMutation({
    mutationKey: ['send_email_otp'],
    mutationFn: sendEmailOtp,
    onError: (error) => {
      console.error('Send email OTP error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to send verification code'
      );
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['login'],
    mutationFn: staffLogin,
    onSuccess: (data) => {
      queryClient.setQueryData(['user'], data);
      toast.success('Successfully logged in!');
    },
    onError: (error) => {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Failed to login');
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['logout'],
    mutationFn: staffLogout,
    onSuccess: () => {
      queryClient.setQueryData(['user'], null);
      toast.success('Successfully logged out!');
    },
    onError: (error) => {
      console.error('Logout error:', error);
      toast.error(error.response?.data?.message || 'Failed to logout');
    },
  });
};

export const useForgotPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['forgotpassword'],
    mutationFn: forgotPassword,
    onSuccess: (data) => {
      queryClient.setQueryData(['forgotPassword'], data);
      toast.success('Email sent!');
    },
    onError: (error) => {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Failed to login');
    },
  });
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['resetpassword'],
    mutationFn: resetPassword,
    onSuccess: (data) => {
      queryClient.setQueryData(['passwordReset'], data);
      toast.success('Password reset successful!');
    },
    onError: (error) => {
      console.error('Password reset error:', error);
      toast.error(error.response?.data?.message || 'Failed to reset password');
    },
  });
};

export const useCreateStaffNotification = () => {
  return useMutation({
    mutationKey: ['create_staff_notification'],
    mutationFn: createStaffNotification,
    onSuccess: (data) => {
      toast.success('Notification sent successfully');
      return data;
    },
    onError: (error) => {
      console.error('Error creating staff notification:', error);
      toast.error(
        error.response?.data?.message || 'Failed to send notification'
      );
    },
  });
};

export const useChangeEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['staff_change_email'],
    mutationFn: staffChangeEmail,
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Email changed successfully!');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useSendChangeEmailOtp = () => {
  return useMutation({
    mutationKey: ['staff_send_change_email_otp'],
    mutationFn: staffSendEmailChangeOtp,
    onSuccess: () => {
      toast.success('Verification code sent to your new email');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};
